import React, { lazy } from 'react'
import { withTitle } from '../../components/common/titleComp/TitleComp'
import { Navigate, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import Header from 'components/common/header'
import CustomScroll from './customScroll'
import ReOrderAlertMessage from 'components/common/cateringReOrderAlert'
import { util } from 'utils'

const Drawer = lazy(() => import('components/common/drawer'))
const Account = lazy(() => import('components/account/MobileProfile/Account'))
const Landing = lazy(() => import('components/landing/Landing'))
const Home = lazy(() => import('components/home'))
const Deals = lazy(() => import('components/deals'))
const ReferAFriend = lazy(() => import('components/referAFriend'))
const Shop = lazy(() => import('components/shop'))
const Catering = lazy(() => import('components/catering'))
const Orders = lazy(() => import('components/orders'))
const CategoryDetails = lazy(() => import('components/categoryDetails'))
const SeeAll = lazy(() => import('components/common/seeAll'))
const ProductDetail = lazy(() => import('components/common/productDetail'))
const SearchResults = lazy(() => import('components/common/searchResults'))
const Checkout = lazy(() => import('components/checkout'))
const OrderDetail = lazy(() => import('components/orderDetail'))
const orderStatus = lazy(() => import('components/orderStatus'))
const UserAccount = lazy(() => import('components/userAccount'))
const WebSiteDown = lazy(() => import('components/websiteDown'))
const Faqs = lazy(() => import('components/common/faqs'))
const StoreContactInfo = lazy(() => import('components/common/storeContactInfo'))
const CancelOrder = lazy(()=>import('components/cancelOrder'))
const CateringSeeAll = lazy(() => import('components/common/cateringSeeAll'))

function CustomRedirectedRoutes({ from, to, location, index }) {
    const item = location?.pathname.split('/')[index]
    return <Navigate from={from} to={{ pathname: to.replace(':replace', item.toLowerCase()) }} />
}

const CateringRouteGuard = ({ children }) => {
    const { isSignedIn } = util.getJWTDetails()
    const userProfile = useSelector(state => state.user.get('userProfile'))
    const preorderEnabledForGuestUser = useSelector(state =>
        state.configuration.get('is_preorder_enabled_for_guest_user')
    )
    const preOrderPilotUsers = useSelector(state => state.configuration.get('preOrderPilotUsers'))

    const isGuestPreorderEnabled = preorderEnabledForGuestUser === true

    const pilotUsersEnabled =
        preOrderPilotUsers?.get?.('enabled') === true || preOrderPilotUsers?.enabled === true
    const pilotUserValuesRaw = preOrderPilotUsers?.get?.('value') ?? preOrderPilotUsers?.value ?? []
    const pilotUserValues =
        typeof pilotUserValuesRaw?.toJS === 'function' ? pilotUserValuesRaw.toJS() : pilotUserValuesRaw
    const loyaltyId = userProfile?.get?.('loyalty_id') ?? userProfile?.loyalty_id
    const canSignedInUserSeeCateringTab =
        pilotUsersEnabled &&
        !!loyaltyId &&
        (pilotUserValues.length === 0 || pilotUserValues.map(String).includes(String(loyaltyId)))

    const showCateringTab = isSignedIn ? canSignedInUserSeeCateringTab : isGuestPreorderEnabled

    if (!showCateringTab) {
        return <Navigate to="/home" replace />
    }

    return children
}

function CustomRedirectedDynamicRoutes(props) {
    const navigate = useNavigate()
    let newPath = props.to
    Object.keys(props.computedMatch.params).forEach(key => {
        newPath = newPath.replace(`:${key}`, encodeURIComponent(props.computedMatch.params[key]))
    })
    navigate(encodeURI(newPath))
    return null
}

const routes = [
    {
        path: '*',
        element: (
            <CustomRedirectedDynamicRoutes
                from="/products/:category/:productDetails"
                to="/shop/:category/:productDetails"
            />
        ),
    },
    {
        path: '*',
        element: (
            <CustomRedirectedRoutes from="/products/:category" to="/shop/:replace" index={2} />
        ),
    },
    {
        path: '*',
        element: (
            <CustomRedirectedRoutes
                from="/product-details/:productDetails"
                to="/search-results/:replace"
                index={2}
            />
        ),
    },
    { path: 'basket', element: <Navigate to="/home" replace /> },
    { path: 'activate', element: <Navigate to="/home" replace /> },
    { path: 'promotions', element: <Navigate to="/deals" replace /> },
    { path: 'product-search', element: <Navigate to="/search-results" replace /> },
    { path: '7NowGoldPass', element: <Navigate to="/user-account?section=subscription" replace /> },

    ...[
        {
            path: 'appDownload',
            element: withTitle({ component: WebSiteDown, title: 'App Download' })(),
            hideHeader: true,
        },
        {
            path: '',
            element: withTitle({
                component: Landing,
                title: '7Now: 7-Eleven Hawaii Food and Alcohol Delivery 24/7 in about 30 mins',
            })(),
            hideHeader: true,
        },
        {
            path: 'home',
            element: withTitle({
                component: Home,
                title: '7Now: 7-Eleven Hawaii Food and Alcohol Delivery 24/7 in about 30 mins',
            })(),
        },
        {
            path: 'home/:productDetails',
            element: withTitle({ component: ProductDetail, title: 'Product Details' })(),
        },
        {
            path: 'deals',
            element: withTitle({ component: Deals, title: 'Deals' })(),
        },
        {
            path: 'deals/product/:productDetails',
            element: withTitle({ component: ProductDetail, title: 'Product Details' })(),
        },
        {
            path: 'deals/:category',
            element: withTitle({ component: CategoryDetails, title: 'Category Deals' })(),
        },
        {
            path: 'deals/:category/:subcategory/all',
            element: withTitle({ component: SeeAll, title: 'Deals Subcategory Details' })(),
        },
        {
            path: 'deals/:category/:subcategory/all/:product',
            element: withTitle({ component: ProductDetail, title: 'Deals Product Details' })(),
        },
        {
            path: 'deals/:category/:productDetails',
            element: withTitle({ component: ProductDetail, title: 'Product Details' })(),
        },
        {
            path: 'shop',
            element: withTitle({ component: Shop, title: 'Shop' })(),
        },
        {
            path: 'catering',
            element: (
                <CateringRouteGuard>
                    {withTitle({ component: Catering, title: 'Catering' })()}
                </CateringRouteGuard>
            ),
        },
        {
            path: 'catering/:productDetails',
            element: (
                <CateringRouteGuard>
                    {withTitle({ component: ProductDetail, title: 'Catering Product Details' })()}
                </CateringRouteGuard>
            ),
        },
        {
            path: 'catering/:products/all/:productDetails',
            element: (
                <CateringRouteGuard>
                    {withTitle({ component: ProductDetail, title: 'Product Details' })()}
                </CateringRouteGuard>
            ),
        },
        {
            path: 'orders',
            element: withTitle({ component: Orders, title: 'Orders' })(),
        },
        {
            path: 'referafriend',
            element: withTitle({ component: ReferAFriend, title: 'Refer A Friend' })(),
        },
        {
            path: 'shop/:category',
            element: withTitle({ component: CategoryDetails, title: 'Category Details' })(),
        },
        {
            path: 'search-results',
            element: withTitle({ component: SearchResults, title: 'Search Results' })(),
        },
        {
            path: 'search-results/:product',
            element: withTitle({ component: ProductDetail, title: 'Search Results' })(),
        },
        {
            path: 'search-results/:category/all',
            element: withTitle({ component: SeeAll, title: 'Search Results' })(),
        },
        {
            path: 'home/:products/all',
            element: withTitle({ component: SeeAll, title: 'Products' })(),
        },
        {
            path: 'shop/:category/:products/all',
            element: withTitle({ component: SeeAll, title: 'Products' })(),
        },
        {
            path: 'catering/:products/all',
            element: (
                <CateringRouteGuard>
                    {withTitle({ component: CateringSeeAll, title: 'Products' })()}
                </CateringRouteGuard>
            ),
        },
        {
            path: 'shop/:category/:productDetails',
            element: withTitle({
                component: ProductDetail,
                title: 'Product Details',
                isCanonical: true,
            })(),
        },
        {
            path: 'home/:products/:productDetails',
            element: withTitle({ component: ProductDetail, title: 'Product Details' })(),
        },
        {
            path: 'home/:products/all/:productDetails',
            element: withTitle({ component: ProductDetail, title: 'Product Details' })(),
        },
        {
            path: 'shop/:category/:products/all/:productDetails',
            element: withTitle({
                component: ProductDetail,
                title: 'ProductDetails',
                isCanonical: true,
            })(),
        },
        {
            path: 'checkout',
            element: withTitle({ component: Checkout, title: 'Checkout' })(),
            hideHeader: true,
        },
        {
            path: 'order-details',
            element: withTitle({ component: OrderDetail, title: 'Order Details' })(),
        },
        {
            path: 'order-status',
            element: withTitle({ component: orderStatus, title: 'Order Details' })(),
        },
        {
            path: 'order-status/cancel-order',
            element: withTitle({ component: CancelOrder, title: 'Cancel Order' })(),
        },
        {
            path: 'user-account',
            element: withTitle({ component: UserAccount, title: 'User Account' })(),
        },
        {
            path: 'FAQs',
            element: withTitle({ component: Faqs, title: 'FAQs' })(),
        },
        {
            path: 'contact-us',
            element: withTitle({ component: StoreContactInfo, title: 'Contact Us' })(),
        },
        {
            path: 'drawer-account',
            element: withTitle({ component: Account, title: 'Drawer Account' })(),
        },
        {
            path: 'drawer-basket',
            element: withTitle({ component: Drawer, title: 'Drawer Basket' })(),
        },
    ].map(route => {
        const withLayout = route.hideHeader ? (
            route.element
        ) : (
            <>
                <Header />
                <ReOrderAlertMessage />
                <div className="body">
                    <CustomScroll>{route.element}</CustomScroll>
                </div>
            </>
        )
        return {
            path: route.path,
            element: withLayout,
        }
    }),

    { path: '*', element: <Navigate to="/home" replace /> },
]

export default routes
